"use strict";
// Payments — one capability, many consumers, many providers.
//
//   consumers (anything that sells)           engine                providers (who moves money)
//   ─────────────────────────────────         ──────────            ───────────────────────────
//   shop.order        ┐                       ┌──────────┐          ┌ stripe     (intl, Connect)
//   funnel.offer      ├─ payments.purpose@1 ─▶│ payments │◀─ payments.provider@1 ┤ razorpay   (INR, UPI)
//   payments.link     │   price + fulfil      │ checkout │          ├ upi        (direct UPI, owner confirms)
//                                                                       └ cod        (cash on delivery)
//   booking, donation ┘                       └──────────┘
//                                                  │ emits payments.payment.succeeded@1 { purpose, ref }
//
// A consumer never talks to Stripe or Razorpay. It registers a PURPOSE: the
// engine asks the purpose to price a reference server-side (so the browser
// never sets a price) and tells it when the payment succeeds or is refunded.
// Providers advertise FEATURE FLAGS, so a consumer can ask for "a provider
// that can charge off-session" (one-click upsells) and degrade if none can.
Object.defineProperty(exports, "__esModule", { value: true });
exports.UPI_PLUGIN = exports.CASH_ON_DELIVERY_PLUGIN = exports.RAZORPAY_PLUGIN = exports.STRIPE_PLUGIN = exports.PAYMENTS_PLUGIN = exports.PAYMENT_PROVIDER_FEATURES = void 0;
exports.PAYMENT_PROVIDER_FEATURES = [
    'oneTime',
    'subscriptions',
    /** Save a card/UPI mandate for later charges. */
    'savedMethods',
    /** Charge a saved method without the buyer present (one-click upsells). */
    'offSessionCharge',
    /** Recurring debits via a mandate (e.g. RBI e-mandate), not ad-hoc charges. */
    'recurringMandate',
    'refunds',
    'partialRefunds',
    'cards',
    'upi',
    'netbanking',
    'wallets',
    'applePay',
    'googlePay',
    'cashOnDelivery',
    /** Payment is confirmed by the owner, not by a provider webhook (COD, bank transfer). */
    'manualConfirmation',
    /** The buyer submits a payment reference (a UPI UTR) for the owner to check before confirming. */
    'buyerReference',
    /** Buyer is redirected to a provider-hosted page. */
    'hostedRedirect',
    /** Provider renders a modal/embedded widget on the site. */
    'embeddedModal',
    /** Owner connects an account by onboarding link (no keys). */
    'connectOnboarding',
    /** Owner pastes their own API keys. */
    'byoKeys',
    /** Dunea can take an application fee. */
    'platformFee',
    'testMode',
];
exports.PAYMENTS_PLUGIN = {
    id: 'payments',
    version: '1.0.0',
    kind: 'capability',
    trust: 'first-party',
    title: 'Payments',
    description: 'Take payments anywhere in your app: shops, funnels, bookings, donations, buy buttons. Stripe, Razorpay and more.',
    icon: 'i-ph:credit-card',
    surfaces: ['web-app'],
    agentCard: "Payments: server-priced checkout for any purpose (shop orders, funnel offers, buy buttons); card gateways, cash on delivery, and direct UPI into the owner's own UPI ID with no gateway (for Indian sellers who take UPI today); test/live modes (skills/payments.md).",
    declares: {
        'payments.checkout': {
            major: 1,
            cardinality: 'one',
            description: 'Create a checkout for a purpose + reference. Prices come from the purpose, never the client.',
        },
        'payments.provider': {
            major: 1,
            cardinality: 'many',
            description: 'Moves money: creates checkouts, verifies webhooks, refunds.',
            features: exports.PAYMENT_PROVIDER_FEATURES,
        },
        'payments.purpose': {
            major: 1,
            cardinality: 'many',
            description: 'Something that can be paid for: prices a reference and fulfils it when paid.',
            features: ['oneTime', 'subscriptions', 'upsell', 'quantity'],
        },
    },
    provides: {
        'payments.checkout': { major: 1 },
        // Built-in purpose: a simple priced offer ("Buy now", "Book a call — ₹999").
        // Lets any page take money without Shop or Funnels.
        'payments.purpose': {
            major: 1,
            features: { oneTime: true, subscriptions: true, quantity: true },
            meta: { purpose: 'payments.link' },
        },
    },
    requires: {
        'payments.provider': { major: 1 },
    },
    emits: {
        'payments.payment.succeeded': { major: 1, description: 'A payment for { purpose, ref } was captured.' },
        'payments.payment.failed': { major: 1, description: 'A payment attempt failed or expired.' },
        'payments.payment.refunded': { major: 1, description: 'A payment was fully or partially refunded.' },
        'payments.payment.disputed': { major: 1, description: 'The buyer opened a dispute/chargeback.' },
        'payments.payment.reference_submitted': {
            major: 1,
            description: 'A buyer submitted a payment reference (UPI UTR) for a manual payment; the owner should check it.',
        },
        'payments.subscription.updated': { major: 1, description: 'A subscription started, renewed, paused or ended.' },
    },
    // The owner email when a buyer submits a UPI UTR (studio upi-notifier.ts).
    subscribes: ['payments.payment.reference_submitted'],
    permissions: ['payments', 'db:own-tables', 'email:send'],
    settings: [
        {
            id: 'general',
            title: 'Payments',
            fields: [
                { key: 'currency', label: 'Default currency', type: 'text', placeholder: 'INR, USD, GBP…', required: true },
                { key: 'notifyEmail', label: 'Send payment notifications to', type: 'email' },
            ],
        },
    ],
    targets: {
        'web-app': {
            skills: ['payments'],
            // plugins-proxy.ts is the shared Worker → studio forwarder Shop and Funnels also use.
            lockedPaths: [
                'frontend/worker/dunea/plugins-proxy.ts',
                'frontend/worker/dunea/payments.ts',
                'frontend/src/dunea/payments.tsx',
            ],
            detect: ['registerPaymentsRoutes'],
            budgetGzipKb: 20,
        },
    },
};
exports.STRIPE_PLUGIN = {
    id: 'stripe',
    version: '1.0.0',
    kind: 'provider',
    trust: 'first-party',
    title: 'Stripe',
    description: 'Cards, Apple Pay and Google Pay in 40+ countries. Connect your account in one click.',
    icon: 'i-ph:stripe-logo',
    provides: {
        'payments.provider': {
            major: 1,
            features: {
                oneTime: true,
                subscriptions: true,
                savedMethods: true,
                offSessionCharge: true,
                refunds: true,
                partialRefunds: true,
                cards: true,
                wallets: true,
                applePay: true,
                googlePay: true,
                hostedRedirect: true,
                connectOnboarding: true,
                platformFee: true,
                testMode: true,
            },
            // Express accounts aren't available to India-based sellers; INR shops route to Razorpay.
            meta: { currencies: '*', excludeCurrencies: ['INR'] },
        },
    },
    permissions: ['network:api.stripe.com'],
};
exports.RAZORPAY_PLUGIN = {
    id: 'razorpay',
    version: '1.0.0',
    kind: 'provider',
    trust: 'first-party',
    title: 'Razorpay',
    description: 'UPI, cards, netbanking and wallets for India.',
    icon: 'i-ph:currency-inr',
    provides: {
        'payments.provider': {
            major: 1,
            features: {
                oneTime: true,
                subscriptions: true,
                savedMethods: true,
                // RBI rules: recurring debits need an e-mandate; ad-hoc one-click upsells are not allowed.
                offSessionCharge: false,
                recurringMandate: true,
                refunds: true,
                partialRefunds: true,
                cards: true,
                upi: true,
                netbanking: true,
                wallets: true,
                embeddedModal: true,
                byoKeys: true,
                testMode: true,
            },
            meta: { currencies: ['INR'] },
        },
    },
    permissions: ['network:api.razorpay.com'],
    settings: [
        {
            id: 'keys',
            title: 'Razorpay keys',
            description: 'Dashboard → Account & Settings → API Keys. Test keys start with rzp_test_, live keys with rzp_live_.',
            fields: [
                { key: 'keyId', label: 'Key ID', type: 'text', required: true, perEnvironment: true },
                { key: 'keySecret', label: 'Key secret', type: 'secret', required: true, perEnvironment: true },
                { key: 'webhookSecret', label: 'Webhook secret', type: 'secret', required: true, perEnvironment: true },
            ],
        },
    ],
};
exports.CASH_ON_DELIVERY_PLUGIN = {
    id: 'cod',
    version: '1.0.0',
    kind: 'provider',
    trust: 'first-party',
    title: 'Cash on delivery',
    description: 'Let buyers pay when the order arrives. You confirm payment when you collect it.',
    icon: 'i-ph:money',
    provides: {
        'payments.provider': {
            major: 1,
            features: { oneTime: true, cashOnDelivery: true, manualConfirmation: true, testMode: true },
            meta: { currencies: '*' },
        },
    },
};
/**
 * Direct UPI: the buyer pays the owner's own UPI ID (any UPI app, by QR or
 * app link), with the exact amount and order in the request. No gateway, no
 * KYC, no fees. Nothing reports the transfer back, so the buyer submits the
 * 12-digit UTR from their app and the owner confirms it against their UPI
 * app or bank statement. Unpaid checkouts expire like any other; once a UTR
 * is in, the payment waits for the owner.
 */
exports.UPI_PLUGIN = {
    id: 'upi',
    version: '1.0.0',
    kind: 'provider',
    trust: 'first-party',
    title: 'UPI (direct)',
    description: 'Take UPI payments straight into your own UPI ID, with no payment gateway or fees. Buyers scan a QR or open their UPI app with the amount filled in, then share the UTR; you confirm it.',
    icon: 'i-ph:qr-code',
    provides: {
        'payments.provider': {
            major: 1,
            features: { oneTime: true, upi: true, manualConfirmation: true, buyerReference: true, testMode: true },
            meta: { currencies: ['INR'] },
        },
    },
    settings: [
        {
            id: 'upi',
            title: 'UPI ID',
            description: 'The UPI ID payments go to, e.g. yourshop@okaxis. A business UPI ID (PhonePe Business, Paytm Business, Google Pay for Business) is free and works more reliably with every app than a personal one.',
            fields: [
                { key: 'vpa', label: 'UPI ID', type: 'text', required: true },
                { key: 'payeeName', label: 'Name shown to buyers', type: 'text', required: true },
            ],
        },
    ],
};
